import { useRegisterSW } from 'virtual:pwa-register/react'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { usePostHog } from 'posthog-js/react'
import { type ReactElement, Suspense, useContext, useEffect } from 'react'
import { useCurrentUserInfos, useUserTokenRefresh } from '~/api/queries/user'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { AuthContext } from '~/utils/contextes'
import { routeTree } from './routeTree.gen'
import { themeClass } from './styles/theme.css'

const defaultNotFoundComponent = (): ReactElement => (
  <NoResult
    message="Aucune page ne correspond à cette adresse."
    onDismiss={() => (window.location.href = '/')}
    dismissLabel="Retour au tableau de bord"
  />
)

const router = createRouter({
  routeTree,
  defaultNotFoundComponent,
  context: {
    currentUser: undefined
  },
  stringifySearch: (search) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(search)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      }
    }
    const str = params.toString()
    return str ? `?${str}` : ''
  },
  parseSearch: (searchStr) => {
    const result: Record<string, string> = {}
    for (const [key, value] of new URLSearchParams(searchStr).entries()) {
      result[key] = value
    }
    return result
  }
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const Main = (): ReactElement => {
  const currentUser = useContext(AuthContext)
  return (
    <Suspense fallback={<Loader />}>
      <RouterProvider router={router} context={{ currentUser }} />
    </Suspense>
  )
}

export const App = (): ReactElement => {
  const posthog = usePostHog()
  const { data: userData, isFetching } = useCurrentUserInfos()
  const { data: refreshToken, isError, isFetched } = useUserTokenRefresh()

  useRegisterSW()

  useEffect(() => {
    if (
      userData?.[0] &&
      import.meta.env.MODE === 'production' &&
      import.meta.env.VITE_ENABLE_POSTHOG === 'true' &&
      import.meta.env.VITE_PUBLIC_POSTHOG_KEY &&
      import.meta.env.VITE_PUBLIC_POSTHOG_HOST
    ) {
      posthog.identify(userData[0].id.toString(), {
        email: userData[0].email,
        instance: localStorage.getItem('instance') ?? ''
      })
    }
  }, [userData, posthog])

  if (refreshToken?.token !== undefined) {
    localStorage.setItem('token', refreshToken.token)
  }

  if (isFetched && isError) {
    localStorage.removeItem('id')
    localStorage.removeItem('token')
    localStorage.removeItem('instance')
    localStorage.removeItem('email')
  }

  if (
    localStorage.getItem('id') !== null &&
    localStorage.getItem('id') !== '' &&
    isFetching
  ) {
    return <Loader />
  }

  return (
    <AuthContext.Provider value={userData?.[0] ?? null}>
      <div className={themeClass} id="appWrapper">
        <Main />
      </div>
    </AuthContext.Provider>
  )
}
